import React from 'react';
import { useForm } from 'react-hook-form';
import { Link, useHistory } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import PublicRoutes from '@/constants/routing/PublicRoutes';
import translate from '@/lib/intl/translate';
import { emailRegex, userNotConfirmedError } from './authConstants';

const { VERIFICATION, RESET_PASSWORD } = PublicRoutes;
const EMAIL_INPUT_PROPS = {
  'data-testid': 'email-input',
};

function LoginForm({ error, loginAction, clearLoginErrors }) {
  const history = useHistory();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const [showPassword, setShowPassword] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const watchEmail = watch('email');

  if (error && error.code === userNotConfirmedError) {
    clearLoginErrors();
    history.push(`${VERIFICATION}?username=${encodeURIComponent(watchEmail)}`);
  }

  const handleFormSubmit = React.useCallback(
    (data) => {
      try {
        if (isSubmitting) {
          return;
        }

        setIsSubmitting(true);
        clearLoginErrors();
        const { email, password } = data;

        loginAction(email, password);
      } catch (e) {
        setIsSubmitting(false);
      }
    },
    [isSubmitting],
  );
  const handleFormSubmitError = () => {
    setIsSubmitting(false);
  };

  const { ref: emailRef, ...emailRest } = register('email', {
    required: translate('VALIDATION_ORG_EMAIL_REQUIRED'),
    pattern: {
      // eslint-disable-next-line
      value: emailRegex,
      message: translate('VALIDATION_INVALID_EMAIL'),
    },
  });
  const { ref: passwordRef, ...passwordRest } = register('password', {
    required: {
      value: true,
      message: translate('VALIDATION_PASSWORD_REQUIRED'),
    },
  });

  return (
    <div data-testid="login-form" className="flex flex-col">
      <div className="flex pb-4">
        <Typography variant="h1">{translate('SIGN_IN')}</Typography>
      </div>
      <div className="flex flex-col">
        <Collapse in={!!error}>
          <div className="pb-4">
            <Alert severity="error">{error?.message}</Alert>
          </div>
        </Collapse>
        <form onSubmit={handleSubmit(handleFormSubmit, handleFormSubmitError)}>
          <TextField
            className="mb-3"
            InputProps={EMAIL_INPUT_PROPS}
            fullWidth
            error={!!errors.email}
            helperText={errors.email ? errors.email.message : ''}
            label={translate('LOGIN_EMAIL_LABEL')}
            onChange={clearLoginErrors}
            type="email"
            inputRef={emailRef}
            {...emailRest}
          />
          <TextField
            fullWidth
            error={!!errors.password}
            helperText={errors.password ? errors.password.message : ''}
            id="outlined-adornment-password"
            InputProps={{
              'data-testid': 'password-input',
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    size="large"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            type={showPassword ? 'text' : 'password'}
            label={translate('PASSWORD')}
            inputRef={passwordRef}
            {...passwordRest}
          />
          <div className="flex w-full justify-end pt-1 text-grey-900 no-underline type-body4">
            <Link to={{ pathname: RESET_PASSWORD }}>
              {translate('FORGOT_PASSWORD')}
            </Link>
          </div>
          <div className="mt-8 text-center">
            <LoadingButton
              className="w-full px-16 sm:w-auto"
              data-testid="submit-button"
              loading={isSubmitting}
              type="submit"
              variant="contained"
            >
              {translate('SIGN_IN')}
            </LoadingButton>
          </div>
        </form>
      </div>
    </div>
  );
}

LoginForm.propTypes = {
  loginAction: PropTypes.func.isRequired,
  clearLoginErrors: PropTypes.func.isRequired,
  error: PropTypes.shape({ code: {}, message: {} }),
};

LoginForm.defaultProps = {
  error: null,
};

export default LoginForm;
